import { useAppDispatch, useAppSelector } from "app/hooks";
import { useCallback, useMemo } from "react";
import {
  addCallToIgnoreList,
  loadCallQueueAction,
  onHoldCallAction,
  outgoingCallDisplayDigitsAction,
  pickUpCallAction,
  removeCallFromQueueAction,
  removeIncomingCallFromQueueAction,
  removeCallsInProgressAction,
  resumeCallAction,
  selectDialerActiveCall,
  selectDialerCallsQueue,
  selectDialerDisplaySettings,
  selectDialerIncomingCall,
  selectDialerIncomingCallsQueue,
  selectOutgoingCallDetails,
  setDefaultDisplayMode,
  setOutgoingCallIsDialingAction,
  setOutgoingCallPatientAction,
  setOutgoingCallPhoneNumberAction,
  swapCallAction,
  toggleExpandAction,
  toggleMuteActiveCallAction,
  toggleOutgoingCallDisplayPatientsAction,
  toggleShowQueueAction,
  toggleShowSettingsAction,
  updateCreateCallDetailsAction,
} from "./suno-dialer-slice";
import { selectCurrentUser } from "features/auth/auth-slice";
import { type DialerCallerDetail } from "./types";
import {
  DIALER_CALL_STATE_ON_HOLD,
  DIALER_VIEW_MODE_EXPANDED,
} from "./constants";
import {
  DEFAULT_PATIENTS_QUERY,
  useGetPatientQuery,
  useLazyQueryPatientsQuery,
} from "services/patients";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import { useGetAppointmentQuery } from "services/appointments";
import { useCallContext } from "hooks/use-twilio-call-context";
import { type Patient } from "services/types/patients.type";
import { useTwilioContext } from "hooks/use-twilio-context.hook";
import { normalizePhoneNumber } from "./utils";
import { Call } from "@twilio/voice-sdk";
import { useLogCallEventMutation } from "services/suno-comms/suno-comms-api";
import useSyncBetweenTabs from "hooks/use-sync-between-tabs.hook";

export default function useSunoDialer() {
  const currentUser = useAppSelector(selectCurrentUser);
  const { twilio } = useTwilioContext();
  const [logCallEvent] = useLogCallEventMutation();

  const dispatch = useAppDispatch();
  const { currentCall, setCurrentCall, setLastCallSid } = useCallContext();

  const displaySettings = useAppSelector(selectDialerDisplaySettings);

  const incomingCallsQueue = useAppSelector(selectDialerIncomingCallsQueue);

  const { incomingCall, incomingCallDetails } = useAppSelector(
    selectDialerIncomingCall
  );

  const { activeCall, activeCallDetails } = useAppSelector(
    selectDialerActiveCall
  );

  const outgoingCall = useAppSelector(selectOutgoingCallDetails);

  const callsQueue = useAppSelector(selectDialerCallsQueue);

  useSyncBetweenTabs<{ action: string; payload: any }>(
    ({ action, payload }) => {
      if (action === "ignore_call") {
        dispatch(addCallToIgnoreList({ sid: payload }));
      }
    }
  );

  const canSwapCurrentCall = useMemo(
    () => !activeCall || activeCallDetails?.state === DIALER_CALL_STATE_ON_HOLD,
    [activeCall, activeCallDetails?.state]
  );

  const activeCallerId = useMemo(() => {
    return activeCall
      ? activeCallDetails?.callerId
      : incomingCallDetails?.callerId;
  }, [activeCall, activeCallDetails?.callerId, incomingCallDetails?.callerId]);

  const { data: patient, isFetching: isPatientFetching } = useGetPatientQuery(
    { patientId: Number(activeCallerId!), query: PATIENT_HEADER_QUERY },
    {
      skip: !Boolean(activeCallerId),
    }
  );

  const { data: upcomingAppointment = null } = useGetAppointmentQuery(
    { id: patient?.next_appointment?.id ?? -1 },
    {
      skip: !patient?.next_appointment?.id,
    }
  );

  const { data: pastAppointment = null } = useGetAppointmentQuery(
    { id: patient?.previous_appointment?.id ?? -1 },
    {
      skip: !patient?.previous_appointment?.id,
    }
  );

  const { is_past_appointment: isPastAppointment, appointment } =
    useMemo(() => {
      return upcomingAppointment
        ? { is_past_appointment: false, appointment: upcomingAppointment }
        : { is_past_appointment: true, appointment: pastAppointment };
    }, [pastAppointment, upcomingAppointment]);

  const handleLoadCallQueue = useCallback(
    (data: DialerCallerDetail[]) => {
      dispatch(loadCallQueueAction(data));
    },
    [dispatch]
  );

  const handleToggleExpand = useCallback(() => {
    dispatch(toggleExpandAction());
  }, [dispatch]);

  const handleToggleShowQueue = useCallback(() => {
    dispatch(toggleShowQueueAction());
  }, [dispatch]);

  const handleToggleMuteActiveCall = useCallback(() => {
    if (!currentCall) return;
    const shouldMute = !currentCall.isMuted();
    currentCall.mute(shouldMute);
    dispatch(toggleMuteActiveCallAction({ shouldMute }));
  }, [currentCall, dispatch]);

  const handleResumeCall = useCallback(
    (id: string) => {
      dispatch(resumeCallAction(id));
    },
    [dispatch]
  );

  const handleHoldCall = useCallback(
    (id: string) => {
      dispatch(onHoldCallAction(id));
    },
    [dispatch]
  );

  const handlePickUpCall = useCallback(
    async (id: string, call?: Call | null) => {
      if (call) {
        call?.accept();
        dispatch(
          pickUpCallAction({
            id,
            userId: currentUser?.id,
          })
        );
        const { parameters } = call;
        const callSid = parameters.CallSid;

        dispatch(
          updateCreateCallDetailsAction({
            callSid,
            isShouldExecuteUpdate: true,
          })
        );
      }
    },
    [currentUser?.id, dispatch]
  );

  const handleDismissCall = useCallback(
    (id: string | undefined) => {
      if (id && currentCall) {
        dispatch(removeCallFromQueueAction(id));
        currentCall?.reject();
        currentCall?.disconnect();
        setCurrentCall(null);
        setLastCallSid?.(id);
      }
    },
    [currentCall, dispatch, setCurrentCall, setLastCallSid]
  );

  const handleEndCall = useCallback(
    (id: string | undefined) => {
      dispatch(removeCallsInProgressAction());
      currentCall?.disconnect();
      setCurrentCall(null);
      setLastCallSid?.(id);
    },
    [currentCall, dispatch, setCurrentCall, setLastCallSid]
  );

  const handleEndCallsInProgress = useCallback(() => {
    dispatch(removeCallsInProgressAction());
    currentCall?.disconnect();
    setCurrentCall(null);
  }, [currentCall, dispatch, setCurrentCall]);

  const handleSwapCall = useCallback(
    (toId: string) => {
      dispatch(swapCallAction({ toId }));
    },
    [dispatch]
  );

  const handleChangeOutgoingCallPhoneNumberInput = useCallback(
    (phoneNumber: string) => {
      dispatch(setOutgoingCallPhoneNumberAction({ phoneNumber }));
    },
    [dispatch]
  );

  const handleChangeOutgoingCallPatient = useCallback(
    (patient?: Patient) => {
      dispatch(setOutgoingCallPatientAction({ patient }));
    },
    [dispatch]
  );

  const handleClearIncomingCallFromQueue = useCallback(() => {
    dispatch(removeIncomingCallFromQueueAction());
  }, [dispatch]);

  const [queryPatients] = useLazyQueryPatientsQuery();

  const findExistingPatient = useCallback(
    async (phoneNumber: string, patientId?: number) => {
      return queryPatients(
        {
          ids: patientId ? [patientId] : undefined,
          search: !patientId ? normalizePhoneNumber(phoneNumber) : undefined,
          baseFilter: {},
          is_active: true,
          limit: 1,
          query: DEFAULT_PATIENTS_QUERY,
        },
        false
      ).then(({ data }) => (data?.length ? data[0] : undefined));
    },
    [queryPatients]
  );

  const handleUpdateWidgetStateBeforeMakeCall = useCallback(
    async (phoneNumber: string, patientId?: number) => {
      handleEndCallsInProgress();
      handleChangeOutgoingCallPhoneNumberInput(phoneNumber);
      dispatch(setDefaultDisplayMode({ mode: DIALER_VIEW_MODE_EXPANDED }));

      if (!outgoingCall?.patient) {
        return findExistingPatient(phoneNumber, patientId).then((patient) => {
          if (patient) {
            handleChangeOutgoingCallPatient(patient);
          }
          return patient;
        });
      } else {
        handleChangeOutgoingCallPatient(outgoingCall?.patient);
      }
      return Promise.resolve(outgoingCall?.patient);
    },
    [
      dispatch,
      findExistingPatient,
      handleChangeOutgoingCallPatient,
      handleChangeOutgoingCallPhoneNumberInput,
      handleEndCallsInProgress,
      outgoingCall?.patient,
    ]
  );

  const handleMakeTwilioCall = useCallback(
    async (phoneNumber: string, patient?: Patient): Promise<void> => {
      const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);

      try {
        const device = twilio?.device;

        if (!device) {
          const error = "Error making call (no device)";
          console.error(error);
          return;
        }

        const call = await device
          .connect({
            params: {
              To: normalizedPhoneNumber,
            },
            rtcConstraints: {
              audio: true,
            },
          })
          .catch((error: any) => {
            console.error("Error connecting device:", error);
          });
        if (!call) return;

        dispatch(setOutgoingCallIsDialingAction({}));

        const caller =
          patient && patient.phone === normalizedPhoneNumber ? patient : null;

        call.on("ringing", async function () {
          setCurrentCall(call);
          dispatch(
            setOutgoingCallIsDialingAction({
              id: String(call?.parameters.CallSid),
              userId: currentUser?.id,
              patient: caller,
              phoneNumber: normalizedPhoneNumber,
            })
          );

          // Log a new call event to Suno Comms API
          const { parameters } = call;
          const call_data = {
            event: "ringing",
            direction: "outgoing",
            status: "ringing",
            callSid: parameters?.CallSid,
            fromNumber: device.identity || "",
            toNumber: normalizedPhoneNumber,
          };

          logCallEvent(call_data).then((response) => {
            if ("data" in response) {
              dispatch(
                updateCreateCallDetailsAction({
                  callSid: String(parameters?.CallSid),
                  isCreateExecuted: true,
                })
              );
            }
          });
        });

        call.on("accept", function () {
          // TODO: https://linear.app/suno/issue/SN-8067/outgoing-call-mark-call-status-answered-before-user-picks-up-the-call
          handlePickUpCall(String(call?.parameters.CallSid), call);
        });
        call.on("cancel", function () {
          handleEndCallsInProgress();
        });
        call.on("disconnect", function () {
          handleEndCallsInProgress();
        });
        call.on("error", function (error: any) {
          handleEndCallsInProgress();
          console.error("Error during a call:", error);
        });
      } catch (error) {
        console.error("Error making call:", error);
      }
    },
    [
      currentUser?.id,
      dispatch,
      handleEndCallsInProgress,
      handlePickUpCall,
      logCallEvent,
      setCurrentCall,
      twilio?.device,
    ]
  );

  const handleMakeCall = useCallback(
    async (phoneNumber: string, patientId?: number): Promise<void> => {
      handleUpdateWidgetStateBeforeMakeCall(phoneNumber, patientId).then(
        (patient) => {
          handleMakeTwilioCall(phoneNumber, patient);
        }
      );
    },
    [handleMakeTwilioCall, handleUpdateWidgetStateBeforeMakeCall]
  );

  const handleToggleShowSettings = useCallback(() => {
    dispatch(toggleShowSettingsAction());
  }, [dispatch]);

  const handleToggleShowPatients = useCallback(() => {
    dispatch(toggleOutgoingCallDisplayPatientsAction());
  }, [dispatch]);

  const handleOutgoingCallDisplayDigits = useCallback(() => {
    dispatch(outgoingCallDisplayDigitsAction());
  }, [dispatch]);

  const isExpanded = useMemo(() => {
    if (activeCall) {
      return activeCall?.displayMode === DIALER_VIEW_MODE_EXPANDED;
    } else if (incomingCall) {
      return incomingCall?.displayMode === DIALER_VIEW_MODE_EXPANDED;
    }
    return displaySettings.defaultDisplayMode === DIALER_VIEW_MODE_EXPANDED;
  }, [activeCall, displaySettings.defaultDisplayMode, incomingCall]);

  return {
    displaySettings,
    incomingCallsQueue,
    incomingCall,
    incomingCallDetails,
    activeCall,
    activeCallDetails,
    callsQueue,
    canSwapCurrentCall,
    isExpanded,
    isPastAppointment,
    appointment,
    patient,
    isLoading: isPatientFetching,
    outgoingCall,
    handleOutgoingCallDisplayDigits,
    handleLoadCallQueue,
    handleToggleExpand,
    handleToggleShowQueue,
    handleToggleMuteActiveCall,
    handleResumeCall,
    handleHoldCallAction: handleHoldCall,
    handleDismissCall,
    handleEndCall,
    handleSwapCall,
    handleToggleShowPatients,
    handleMakeCall,
    handlePickUpCall,
    handleChangeOutgoingCallPhoneNumberInput,
    handleChangeOutgoingCallPatient,
    handleClearIncomingCallFromQueue,
    handleEndCallsInProgress,
    handleToggleShowSettings,
  };
}
